import type { AxiosError } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIStatus, AppCode } from '../../../enum';
import { getCSV, getTableFilters, removeEmpty } from '../../../helpers/utils.helper';
import type { BaseDashboardParams, GuestAlertsRegistered, NameValue } from '../../../models/dashboard';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { dateTimeFilterFormat } from '../program-dashboard/program-dashboard.helper';
import { type DashboardBody, getFiltersInfo } from '../program-dashboard/programDashboardSlice';

const { logError } = RaygunErrorHandlerService();

export type Alert = {
  total_count: number;
  id: number;
  sent_time: string;
  sent_by: string;
  title: string;
  message: string;
  number_of_devices: number;
  org_name: string;
  internal: boolean;
  first_alerts_community_name: string;
  type: AlertType;
  dart: boolean;
  org_timezone_name: string | null;
};

export const AlertTypeLabel = {
  natural_disaster: 'Natural disaster',
  public_safety_threat: 'Public safety threat',
  missing_person: 'Missing person',
} as const;

export type AlertType = keyof typeof AlertTypeLabel;

export type AlertParams = {
  type: AlertType | undefined;
  title: string;
  message: string;
  dart: boolean;
  first_alerts_community_id: string | undefined | null;
};

export type AlertDashboardBody = Omit<DashboardBody, 'for_leps' | 'for_service_providers'>;

export type AlertDashboardData = {
  total_guest_alerts_registered: number | null;
  total_alerts_sent: number | null;
  total_alerts_sent_to: number | null;
  first_alerts_types_sent: NameValue[];
  guest_alerts_registered: GuestAlertsRegistered | null;
};

type AlertSliceType = {
  alerts: Alert[];
  dartEndTimestamp: string | null;
  requestAlertsParams: RequestParams<Alert>;
  alertsApiStatus: APIStatus;
  sendAlertApiStatus: APIStatus;
  showModal: boolean;
  alertsStatsApiStatus: APIStatus;
  alertDashboardParams: BaseDashboardParams;
  alertDashboardData: AlertDashboardData;
  filtersInfo: string;
  tableFiltersInfo: string;
  firstAlertsCSVExport: string;
  registeredGuestsCSVExport: string;
};

type RegistrantExportType = {
  date: string;
  guest_id: string;
  user_role: string;
  registered: number;
  org_code: string;
};

const initialState: AlertSliceType = {
  alerts: [],
  dartEndTimestamp: null,
  requestAlertsParams: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<Alert>('sent_time', 'descend'),
    filteredInfo: {},
  },
  alertsApiStatus: APIStatus.IDLE,
  sendAlertApiStatus: APIStatus.IDLE,
  showModal: false,
  alertsStatsApiStatus: APIStatus.IDLE,
  alertDashboardParams: {
    app_code: AppCode.TalkingStick,
    start_date: dayjs().subtract(1, 'month'),
    end_date: dayjs(),
    selected_org_code: 'allOrgs',
  },
  alertDashboardData: {
    total_guest_alerts_registered: null,
    total_alerts_sent: null,
    total_alerts_sent_to: null,
    first_alerts_types_sent: [],
    guest_alerts_registered: null,
  },
  filtersInfo: '',
  tableFiltersInfo: '',
  firstAlertsCSVExport: '',
  registeredGuestsCSVExport: '',
};

export const fetchAlerts = createAsyncThunk<Alert[], undefined, AsyncThunkConfig>(
  'alert/fetchAlerts',
  async (_, thunkAPI) => {
    try {
      const { requestAlertsParams } = thunkAPI.getState().alertSlice;
      const response = (await axios.post('v0_get_logged_alerts', requestAlertsParams)) as Alert[];
      return response ?? [];
    } catch (e) {
      logError(e, ['alertSlice', 'fetchAlerts']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const exportAlerts = createAsyncThunk<Alert[], undefined, AsyncThunkConfig>(
  'alert/exportAlerts',
  async (_, thunkAPI) => {
    try {
      const { requestAlertsParams } = thunkAPI.getState().alertSlice;
      const exportAlertsParam = {
        sortedInfo: requestAlertsParams.sortedInfo,
        filteredInfo: requestAlertsParams.filteredInfo,
      };

      const filtersInfo = getTableFilters(exportAlertsParam.filteredInfo);
      thunkAPI.dispatch(setTableFiltersInfo(filtersInfo));

      const response = (await axios.post('v0_get_logged_alerts', exportAlertsParam)) as Alert[];
      return response ?? [];
    } catch (e) {
      logError(e, ['alertSlice', 'exportAlerts']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const exportRegistrants = createAsyncThunk<RegistrantExportType[], undefined, AsyncThunkConfig>(
  'alert/exportRegistrants',
  async (_, thunkAPI) => {
    try {
      const { start_date, end_date, selected_org_code } = thunkAPI.getState().alertSlice.alertDashboardParams;
      const body: AlertDashboardBody = {
        start_date: start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: selected_org_code === 'allOrgs' ? null : selected_org_code,
        app_code: AppCode.TalkingStick,
      };
      const response = (await axios.post('v0_export_fa_registrants', body)) as RegistrantExportType[];
      return response ?? [];
    } catch (e) {
      logError(e, ['alertSlice', 'exportRegistrants']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendAlert = createAsyncThunk<string, Required<AlertParams>, AsyncThunkConfig>(
  'alert/sendAlert',
  async (alert, thunkAPI) => {
    try {
      const response = (await axios.post('v0_send_alert_notifications', alert)) as string;
      if (alert.dart) {
        thunkAPI.dispatch(fetchDartDeadline());
      }
      return response ?? ''; // id
    } catch (e) {
      logError(e, ['alertSlice', 'sendAlert']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getFirstAlertsStats = createAsyncThunk<AlertDashboardData, undefined, AsyncThunkConfig>(
  'alert/getFirstAlertsStats',
  async (_, thunkAPI) => {
    try {
      const { start_date, end_date, selected_org_code } = thunkAPI.getState().alertSlice.alertDashboardParams;
      const body: AlertDashboardBody = {
        start_date: start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: selected_org_code === 'allOrgs' ? null : selected_org_code,
        app_code: AppCode.TalkingStick,
      };
      const response = await axios.post('v0_get_first_alerts_statistics', body);
      const faOrgs = thunkAPI.getState().orgsSlice.orgsForAdminDic.fa;
      thunkAPI.dispatch(setFiltersInfo(getFiltersInfo(body, faOrgs)));
      return response as unknown as AlertDashboardData;
    } catch (e) {
      logError(e, ['alertSlice', 'getFirstAlertsStats']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

type DartDeadlineResponse = { dart_deadline: string | null };

export const fetchDartDeadline = createAsyncThunk<DartDeadlineResponse, undefined, AsyncThunkConfig>(
  'alert/fetchDartDeadline',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_get_dart_deadline')) as DartDeadlineResponse;
      return response ?? { dart_deadline: null };
    } catch (e) {
      logError(e, ['alertSlice', 'fetchDartDeadline']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const alertSlice = createSlice({
  name: 'alert',
  initialState,
  reducers: {
    handleAlertsTableChange: (state, action: PayloadAction<TableChange<Alert>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<Alert>;
      state.requestAlertsParams.pagination = action.payload.pagination;
      state.requestAlertsParams.sortedInfo = sorter;
      state.requestAlertsParams.filteredInfo = removeEmpty(filters);
    },
    handleParamsChange: (state, action: PayloadAction<Partial<BaseDashboardParams>>) => {
      state.alertDashboardParams = { ...state.alertDashboardParams, ...action.payload };
    },
    clearFilter: (state) => {
      state.requestAlertsParams.pagination = initialState.requestAlertsParams.pagination;
      state.requestAlertsParams.sortedInfo = initialState.requestAlertsParams.sortedInfo;
      state.requestAlertsParams.filteredInfo = initialState.requestAlertsParams.filteredInfo;
    },
    showAlertModal: (state) => {
      state.showModal = true;
    },
    closeAlertModal: (state) => {
      state.showModal = false;
    },
    setFiltersInfo: (state, action: PayloadAction<string>) => {
      state.filtersInfo = action.payload;
    },
    setTableFiltersInfo: (state, action: PayloadAction<string>) => {
      state.tableFiltersInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending, (state, _action) => {
        state.alertsApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload;
        state.alertsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(exportAlerts.fulfilled, (state, action) => {
        const headers =
          'total_count,id,message,title,sent_time,sent_by,number_of_devices,org_name,org_timezone_name,type,internal,first_alerts_community_name,dart';
        state.firstAlertsCSVExport = getCSV(action.payload, headers.split(','));
      })
      .addCase(exportRegistrants.fulfilled, (state, action) => {
        const headers = 'date,total_registered_users,total_unregistered_users,total_users,organization';
        state.registeredGuestsCSVExport = getCSV(action.payload, headers.split(','));
      })
      .addCase(fetchAlerts.rejected, (state, _action) => {
        state.alertsApiStatus = APIStatus.ERROR;
      })
      .addCase(sendAlert.pending, (state, _action) => {
        state.sendAlertApiStatus = APIStatus.PENDING;
      })
      .addCase(sendAlert.fulfilled, (state, _action) => {
        state.sendAlertApiStatus = APIStatus.FULFILLED;
      })
      .addCase(sendAlert.rejected, (state, _action) => {
        state.sendAlertApiStatus = APIStatus.ERROR;
      })
      .addCase(getFirstAlertsStats.pending, (state, _action) => {
        state.alertsStatsApiStatus = APIStatus.PENDING;
      })
      .addCase(getFirstAlertsStats.fulfilled, (state, action) => {
        state.alertDashboardData = action.payload;
        state.alertsStatsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getFirstAlertsStats.rejected, (state, _action) => {
        state.alertsStatsApiStatus = APIStatus.ERROR;
      })
      .addCase(fetchDartDeadline.fulfilled, (state, action) => {
        state.dartEndTimestamp = action.payload.dart_deadline;
      });
  },
});

export const {
  clearFilter,
  closeAlertModal,
  handleAlertsTableChange,
  handleParamsChange,
  setFiltersInfo,
  setTableFiltersInfo,
  showAlertModal,
} = alertSlice.actions;
